import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useJsConsumer } from '@/hooks/useJsConsumer';
import { useUserPrefsSync } from '@/hooks/useUserPrefs';
import { useConnectionStore } from '@/store/connectionStore';
import { useMachineCount, useMachineStore } from '@/store/machineStore';
import { CompanySwitcher } from './CompanySwitcher';
import { ErrorBoundary } from './ErrorBoundary';
import { ToastStack } from '@/components/notifications/ToastStack';
import { useAlertEventsHydration } from '@/hooks/useAlertEvents';
import { useFlagDefs, useSensorRegistry } from '@/features/sensors/hooks';
import { InstallHint } from './InstallHint';
import { cn } from '@/lib/cn';
import { useBreakpoint } from '@/lib/deviceTier';
import { navDestinations } from '@/view-models/navDestinations';
import { useNavGates } from '@/hooks/useNavGates';
import { NAV_ICONS } from './navIcons';
import { MobileTabBar } from './MobileTabBar';
import { initAudit, recordAudit } from '@/lib/audit';

function StatusPill() {
  // Read-only view of the consumer status. Autostart is owned by
  // AppShell itself (see below) — was previously bootstrapped inside
  // this component, which broke on mobile where the sidebar (and so
  // this pill) is hidden by default and the consumer never started.
  const jsStatus = useConnectionStore((s) => s.status);
  const status: 'connecting' | 'connected' | 'disconnected' | 'error' | 'no-scope' =
    jsStatus === 'live'         ? 'connected'
    : jsStatus === 'connecting' ? 'connecting'
    : jsStatus === 'reconnecting' ? 'connecting'
    : jsStatus === 'no-scope'   ? 'no-scope'
    : jsStatus === 'error'      ? 'error'
    :                              'disconnected';
  const { live, total } = useMachineCount();
  // Don't subscribe to frameCount — would re-render this pill on every SSE
  // frame. Read it via getState() inside the interval instead.
  const lastRef = useRef({ count: 0, ts: Date.now() });
  const [rate, setRate] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      const cur = useMachineStore.getState().frameCount;
      const now = Date.now();
      const dt  = (now - lastRef.current.ts) / 1000;
      const d   = cur - lastRef.current.count;
      setRate(dt > 0 ? d / dt : 0);
      lastRef.current = { count: cur, ts: now };
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const dot =
    status === 'connected'    ? 'bg-[var(--accent-healthy)] shadow-[var(--glow-live)]' :
    status === 'connecting'   ? 'bg-[var(--accent-warning)]' :
    status === 'error'        ? 'bg-[var(--accent-destructive)]' :
    status === 'no-scope'     ? 'bg-[var(--accent-warning)]' :
                                'bg-[var(--fg-faint)]';
  const label = status === 'no-scope' ? 'pick a company' : status;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--bg-base)] border border-[var(--border-subtle)] text-xs">
      <span className={cn('w-2 h-2 rounded-full', dot)} />
      <span className="text-[var(--fg-muted)]">{label}</span>
      <span className="ml-auto tabular font-display flex items-center gap-2">
        <span title="frames per second" className="text-[var(--fg-faint)]">{rate.toFixed(1)}/s</span>
        <span>
          <span className="text-[var(--accent-healthy)]">{live}</span>
          <span className="text-[var(--fg-faint)]">/{total}</span>
        </span>
      </span>
    </div>
  );
}

// `navless` drops the primary nav links — used for the mobile overflow drawer,
// where the bottom tab bar already owns navigation and the drawer is just the
// company switcher + account controls.
function Sidebar({ onClose, navless = false }: { onClose?: () => void; navless?: boolean }) {
  const user   = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Same nav policy the mobile tab bar uses — one pure selector over the gates.
  const visibleNav = navDestinations(useNavGates());

  return (
    <aside className="w-60 shrink-0 h-full bg-[var(--bg-surface)] border-r border-[var(--border-subtle)] flex flex-col">
      <div className="px-4 py-4 border-b border-[var(--border-subtle)]">
        <div className="flex items-center gap-2">
          <img
            src="/phos-logo.png"
            alt=""
            width={30}
            height={30}
            className="select-none shrink-0"
            style={{ filter: 'drop-shadow(0 2px 6px rgba(99,102,241,0.45))' }}
          />
          <div className="font-display font-semibold tracking-tight text-[var(--fg-primary)]">
            Phos<span className="text-[var(--fg-muted)]">/cloud</span>
          </div>
          {onClose && (
            <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-[var(--bg-elevated)]" aria-label="Close menu">
              <X className="w-4 h-4 text-[var(--fg-muted)]" />
            </button>
          )}
        </div>
      </div>

      {navless ? (
        <div className="flex-1" />
      ) : (
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {visibleNav.map(({ id, to, label }) => {
            const Icon = NAV_ICONS[id];
            return (
              <NavLink
                key={id}
                to={to}
                end={to === '/'}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-[var(--bg-elevated)] text-[var(--fg-primary)] font-medium'
                      : 'text-[var(--fg-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--fg-primary)]',
                  )
                }
              >
                <Icon className="w-4 h-4" aria-hidden />
                {label}
              </NavLink>
            );
          })}
        </nav>
      )}

      <div className="px-3 pb-3 space-y-2">
        <CompanySwitcher />
        <StatusPill />
        <InstallHint />
        <div className="px-3 py-2 rounded-md border border-[var(--border-subtle)] text-xs">
          <div className="text-[var(--fg-muted)] truncate">{user?.email}</div>
          <button
            onClick={() => { recordAudit('logout'); logout(); }}
            className="mt-1.5 flex items-center gap-1.5 text-[var(--fg-faint)] hover:text-[var(--accent-destructive)] transition-colors"
          >
            <LogOut className="w-3 h-3" />
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}

// Shown instead of the routed page when there's no active company — an admin
// who hasn't picked one yet (dashboard-web derives an admin's company solely
// from the company switcher). Without this, every company-scoped fetch 400s
// and the page looks broken rather than telling the operator what to do.
function NoCompanyPrompt({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="h-full grid place-items-center p-6 text-center">
      <div className="max-w-sm space-y-3">
        <div className="text-base font-display font-semibold text-[var(--fg-primary)]">
          {isAdmin ? 'Select a company' : 'No company on your account'}
        </div>
        <p className="text-sm text-[var(--fg-muted)] leading-relaxed">
          {isAdmin
            ? 'Choose a company to view its fleet, history, and alerts.'
            : 'Your account isn’t associated with a company yet. Sign out and back in, or contact support.'}
        </p>
        {/* Inline switcher so an admin can pick a company without the (now
            removed) mobile drawer. */}
        {isAdmin && (
          <div className="flex justify-center">
            <CompanySwitcher />
          </div>
        )}
      </div>
    </div>
  );
}

export function AppShell() {
  const { isDesktop } = useBreakpoint();
  const isMobile = !isDesktop; // phone + tablet share the tab-bar shell
  const company = useAuthStore((s) => s.company);
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false);
  const hasCompany = !!company;

  // activity audit: session start + per-route page views
  const location = useLocation();
  useEffect(() => {
    initAudit(() => useAuthStore.getState().user?.token ?? null);
    recordAudit('login');
  }, []);
  useEffect(() => {
    recordAudit('page_view', location.pathname);
  }, [location.pathname]);

  // JetStream consumer autostart. Lifted here from StatusPill — the
  // pill lives inside Sidebar, which is unmounted on mobile, so the
  // consumer never started and cards rendered as "offline" on every
  // phone/tablet. Now bootstraps unconditionally as long as the app
  // shell itself is mounted (i.e. user is authed past AuthGate).
  useJsConsumer(true);

  // Multi-device sync for user preferences (setpoints, view choices,
  // pinned sensors). Hydrates the local stores from the server's
  // blob on auth and debounces local edits back up.
  useUserPrefsSync();

  // Hydrate notification store from REST on auth (SSE keeps it live afterwards).
  useAlertEventsHydration();

  // Load the tenant's sensor display-name overrides once on app entry and push
  // them into the resolver cache (issue #4), so every labeled view (charts,
  // tables, tooltips, rule picker) renders the company's localized names.
  useSensorRegistry();
  useFlagDefs();

  return (
    <div className="h-dvh flex bg-[var(--bg-base)] text-[var(--fg-primary)]">
      {!isMobile && <Sidebar />}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* No top header / hamburger on mobile — the per-page PageHeader is the
            header and the bottom tab bar owns nav. Company switch + sign out
            live in the per-page Controls sheet (and inline in the no-company
            prompt for admins). */}
        <main className="flex-1 min-h-0 overflow-auto">
          <ErrorBoundary>
            {hasCompany
              ? <Outlet />
              : <NoCompanyPrompt isAdmin={isAdmin} />}
          </ErrorBoundary>
        </main>
        {isMobile && <MobileTabBar />}
      </div>
      <ToastStack />
    </div>
  );
}
